import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Store } from "lucide-react";
import { Seo } from "@/components/Seo";

export default function AuthPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [resetting, setResetting] = useState(false);

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  if (!loading && user) return <Navigate to="/" replace />;

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
    setBusy(false);
    if (error) {
      if (error.message.toLowerCase().includes("invalid login credentials")) {
        return toast.error("Invalid email or password.");
      }
      return toast.error("Unable to sign in. Please try again.");
    }
    toast.success("Welcome back.");
    nav("/");
  };

  const signUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { display_name: displayName || normalizedEmail.split("@")[0] },
      },
    });
    setBusy(false);
    if (error) {
      if (error.message.toLowerCase().includes("already registered")) {
        return toast.error("This email is already registered. Sign in or reset the password.");
      }
      return toast.error(error.message);
    }

    // Fire-and-forget welcome email. Idempotency key = user id, so the queue
    // never sends a duplicate even if the form is retried.
    const newUserId = data.user?.id;
    if (newUserId) {
      void supabase.functions
        .invoke("send-transactional-email", {
          body: {
            templateName: "welcome",
            recipientEmail: normalizedEmail,
            idempotencyKey: `welcome-${newUserId}`,
            templateData: {
              name: displayName || normalizedEmail.split("@")[0],
            },
          },
        })
        .catch((err) => {
          // Welcome email is non-blocking — log only, never disrupt signup UX.
          console.warn("Failed to enqueue welcome email", err);
        });
    }

    if (!data.session) {
      toast.success("Account created. Check your email to confirm the account, then sign in.");
      return;
    }

    toast.success("Account created. You're signed in.");
    nav("/");
  };

  const resetPassword = async () => {
    if (!normalizedEmail) {
      toast.error("Enter your email first.");
      return;
    }

    setResetting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetting(false);

    if (error) return toast.error(error.message);
    toast.success("Password reset email sent. Check your inbox.");
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4">
      <Seo
        title="Sign in — StoreLedger Pro"
        description="Sign in to StoreLedger Pro to manage your convenience store accounting, inventory, daily close, and POS integrations."
        canonicalPath="/auth"
      />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Store className="h-5 w-5 text-primary" />
          </div>
          <span className="text-lg font-semibold tracking-tight">StoreLedger Pro</span>
        </div>

        <div className="glass-card p-6">
          <Tabs defaultValue="signin">
            <TabsList className="grid grid-cols-2 mb-5">
              <TabsTrigger value="signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={signIn} className="space-y-4">
                <div>
                  <Label htmlFor="si-email">Email</Label>
                  <Input id="si-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-card/60" autoCapitalize="none" autoCorrect="off" />
                </div>
                <div>
                  <Label htmlFor="si-pw">Password</Label>
                  <Input id="si-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-card/60" />
                </div>
                <Button type="submit" disabled={busy} className="w-full">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
                </Button>
                <Button type="button" variant="ghost" onClick={resetPassword} disabled={resetting} className="w-full">
                  {resetting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reset password
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={signUp} className="space-y-4">
                <div>
                  <Label htmlFor="su-name">Display name</Label>
                  <Input id="su-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-1.5 bg-card/60" placeholder="Casey Operator" />
                </div>
                <div>
                  <Label htmlFor="su-email">Email</Label>
                  <Input id="su-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-card/60" autoCapitalize="none" autoCorrect="off" />
                </div>
                <div>
                  <Label htmlFor="su-pw">Password</Label>
                  <Input id="su-pw" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-card/60" />
                  <p className="text-[11px] text-muted-foreground mt-1.5">Min 8 characters. Leaked-password check enabled.</p>
                </div>
                <Button type="submit" disabled={busy} className="w-full">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  New accounts get the <span className="text-foreground">cashier</span> role by default.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </main>
  );
}